/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { Link, Tab } from '@backstage/core-components';
import { Box, Tabs, createStyles, makeStyles } from '@material-ui/core';
import TabUI from '@material-ui/core/Tab';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

const useStyles = makeStyles(
  theme =>
    createStyles({
      tabsWrapper: {
        gridArea: 'pageSubheader',
        backgroundColor: theme.palette.background.paper,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        minWidth: 0,
      },
      tabs: { borderBottom: '1px solid #e8e8e8' },
      defaultTab: {
        ...theme.typography.caption,
        padding: theme.spacing(3, 3),
        textTransform: 'uppercase',
        // @ts-ignore
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.secondary,
      },
      selected: {
        color: theme.palette.text.primary,
      },
      tabRoot: {
        '&:hover': {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
      },
    }),
  { name: 'BackstageHeaderTabs' },
);

type DomainTabsProps = {
  rootRoute: string;
  tabs: Tab[];
  onChange?: (index: number) => void;
  selectedIndex?: number;
};
export function DomainTabs({
  rootRoute,
  tabs,
  selectedIndex,
  onChange,
}: DomainTabsProps) {
  const [selectedTab, setSelectedTab] = useState<number>(selectedIndex ?? 0);
  const styles = useStyles();

  const params = useParams();

  const handleChange = useCallback(
    (_event: React.ChangeEvent<{}>, index: number) => {
      setSelectedTab(index);
      if (onChange) onChange(index);
    },
    [onChange],
  );

  useEffect(() => {
    if (tabs && tabs.length > 0) {
      let selectedTabIndex = tabs.findIndex(tab => {
        return tab.id === params.domain;
      });
      selectedTabIndex = selectedTabIndex < 0 ? 0 : selectedTabIndex;
      setSelectedTab(selectedTabIndex);
      if (onChange) onChange(selectedTabIndex);
    }
  }, [params.domain, tabs, onChange]);

  useEffect(() => {
    if (selectedIndex !== undefined) {
      setSelectedTab(selectedIndex);
    }
  }, [selectedIndex]);

  return (
    <Box className={styles.tabsWrapper}>
      <Tabs
        indicatorColor="primary"
        textColor="inherit"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="tabs"
        onChange={handleChange}
        value={selectedTab}
        classes={{ root: styles.tabs }}
      >
        {tabs.map((tab: Tab, index: number) => (
          <TabUI
            data-testid={`domain-tab-${index}`}
            label={tab.label}
            key={tab.id}
            component={Link}
            to={`${rootRoute}/${tab.id}`}
            value={index}
            className={styles.defaultTab}
            classes={{ selected: styles.selected, root: styles.tabRoot }}
          />
        ))}
      </Tabs>
    </Box>
  );
}
